// Outreach's people, as one ranked list (Phase 4, item 6). Pure, so `node --test` covers it.
//
// heroes.py already ranked the account's people -- Salesforce contacts and playground members
// with no contact -- so the list is the heroes in the server's order, each joined to its
// contact, then any contact heroes.py did not score, in the server's contact order. The one
// change to that order: the first named person takes the top spot (see `namedFirst`).
import { foundNotCleared, isEnrichedFind } from '../lib/foundByScout.ts';
import type { BookAccount, Contact, FoundByScout, Hero, PlaygroundFacts } from '../api/types.ts';
import { cleanName, contactDisplay, isMaskedName, isSharedInbox } from '../lib/contactName.ts';
import { playgroundContact } from '../lib/playgroundContacts.ts';
import { isQ4Hero } from '../lib/q4Target.ts';

export { cleanName };

export interface OutreachPerson {
  key: string;
  contact: Contact | null;
  hero: Hero | null;
  // Name (real, or read off a `first.last` address), else the address, else a placeholder;
  // `named` says which.
  name: string;
  named: boolean;
  title: string | null;
  email: string | null;
  linkedin: string | null;
  score: number | null;
  because: string | null;
  // Contact-level signal kinds (heroLines labels them), without `in_sequence`.
  tags: string[];
  // Hard stops and caveats, in words.
  flags: string[];
  sequence: string | null;
  playground: PlaygroundFacts | null;
  // Set when Scout's find_contacts found them; such people get their own section.
  foundByScout: FoundByScout | null;
  // What Find looks up and what is ticked: the contact, or for a playground person with no
  // contact a stand-in keyed by their hero key (`playground:<address>`), which the server
  // resolves the same way.
  findTarget: Contact | null;
}

/** Signals from the hero and the contact, once each, without `in_sequence` (shown as a sequence). */
function mergedTags(hero: Hero | null, contact: Contact | null): string[] {
  const kinds = [...(hero?.signals ?? []), ...(contact?.signals ?? [])];
  return [...new Set(kinds)].filter(kind => kind !== 'in_sequence');
}

/** The reasons not to write to a contact, as the People block always worded them. */
export function contactFlags(contact: Contact | null): string[] {
  if (!contact) return [];
  const flags: string[] = [];
  // A Scout-found person says where they came from instead (their own section and chip).
  if (contact.from_mirror === false && !contact.found_by_scout) flags.push('not yet in the Salesforce mirror');
  if (foundNotCleared(contact)) flags.push('not cleared to send');
  if (contact.opted_out) flags.push('opted out');
  if (contact.bounced) flags.push('email bounced');
  if (contact.amplemarket_excluded) flags.push('excluded in Amplemarket');
  return flags;
}

/** A playground member's product facts, looked up by address for a hero with no contact. */
function playgroundByEmail(row: BookAccount, email: string | null): PlaygroundFacts | null {
  if (!email) return null;
  const person = (row.playground_people ?? []).find(member => member.email.toLowerCase() === email.toLowerCase());
  return person?.playground ?? null;
}

/**
 * A playground person with no contact, as the contact shape Find, the push and the builder work
 * on. The id is their hero key, which the server resolves against the account's playground people.
 */
export function playgroundFindTarget(hero: Hero, row: BookAccount | null = null): Contact | null {
  return playgroundContact(hero, row);
}

function personFrom(row: BookAccount, hero: Hero | null, contact: Contact | null): OutreachPerson {
  const email = contact?.email || hero?.email || null;
  const display = contactDisplay(cleanName(contact?.name) || hero?.name, email, 'Unknown person');
  return {
    key: hero?.key ?? `contact:${contact?.id}`,
    contact,
    hero,
    name: display.text,
    named: display.named,
    title: contact?.title || hero?.title || null,
    email,
    linkedin: contact?.linkedin || hero?.linkedin || null,
    score: hero?.score ?? contact?.hero_score ?? null,
    // A Q4 research hero's reason ("GOLD: signed up for Cartesia") stands in for a hero's.
    because: hero?.because ?? contact?.hero_because ?? contact?.found_by_scout?.reason ?? null,
    tags: mergedTags(hero, contact),
    flags: contactFlags(contact),
    sequence: contact?.sequence ?? hero?.in_sequence ?? null,
    playground: contact?.playground ?? playgroundByEmail(row, email),
    foundByScout: contact?.found_by_scout ?? null,
    findTarget: contact ?? (hero ? playgroundFindTarget(hero, row) : null),
  };
}

/** Every person on the account, best first: heroes in rank order, then unscored contacts. */
export function outreachPeople(row: BookAccount): OutreachPerson[] {
  return namedFirst(everyone(row));
}

export interface OutreachSplit {
  // The people to write to, best first.
  people: OutreachPerson[];
  // People Scout's find_contacts found, newest search first, in their own section.
  foundByScout: OutreachPerson[];
  // The Q4 research's heroes: built like Scout finds, listed apart under their own source.
  q4Heroes: OutreachPerson[];
  // Team and role addresses (sales@, support@, ...), kept apart and collapsed.
  sharedInboxes: OutreachPerson[];
}

/**
 * Outreach's people with shared inboxes and Scout's finds set aside, so neither takes the
 * best-person card: a Scout find has no history with us yet.
 */
export function outreachSplit(row: BookAccount): OutreachSplit {
  const all = everyone(row);
  const sharedInboxes = all.filter(person => isSharedInbox(person.email));
  const reachable = all.filter(person => !isSharedInbox(person.email));
  return {
    people: namedFirst(reachable.filter(person => !person.foundByScout)),
    foundByScout: reachable.filter(person => person.foundByScout && !isQ4Hero(person.foundByScout)),
    q4Heroes: reachable.filter(person => isQ4Hero(person.foundByScout)),
    sharedInboxes,
  };
}

/** Heroes in rank order, then unscored contacts, before the named-first move. */
function everyone(row: BookAccount): OutreachPerson[] {
  const contactsById = new Map(row.contacts.map(contact => [contact.id, contact]));
  const covered = new Set<string>();

  // Heroes first, each joined to its contact when it has one.
  const people = (row.heroes ?? []).map(hero => {
    const contact = hero.contact_id ? contactsById.get(hero.contact_id) ?? null : null;
    if (contact) covered.add(contact.id);
    return personFrom(row, hero, contact);
  });

  // Then the contacts heroes.py did not score, in the server's order.
  row.contacts.forEach(contact => {
    if (!covered.has(contact.id)) people.push(personFrom(row, null, contact));
  });
  return people;
}

/**
 * The top spot goes to the first person with a name: an address alone cannot be greeted or
 * drafted to, so it waits in the table, still in rank order. Nobody else moves.
 */
function namedFirst(people: OutreachPerson[]): OutreachPerson[] {
  const firstNamed = people.findIndex(person => person.named);
  if (firstNamed <= 0) return people;
  const named = people[firstNamed]!;
  return [named, ...people.slice(0, firstNamed), ...people.slice(firstNamed + 1)];
}

export interface OrgBadge {
  // A paid Cartesia org, or a free playground account.
  kind: 'org' | 'playground';
  label: 'ORG' | 'PG';
  orgName: string | null;
  plan: string | null;
  role: string | null;
  credits30: number | null;
  lastUsage: string | null;
  // People on this account in the same org; null when the org is not named.
  users: number | null;
}

/** Whether a plan is paid. Free, or no plan on record, is a playground account. */
function isPaidPlan(tier: string | null | undefined): boolean {
  return Boolean(tier) && tier!.toLowerCase() !== 'free';
}

/** People on the account whose product facts name this org. */
export function orgUserCount(people: OutreachPerson[], orgName: string): number {
  return people.filter(person => person.playground?.org_name === orgName).length;
}

/**
 * The ORG / PG badge for a person, or null when they have no product account.
 * Product facts decide when present; else the contact's mirrored org counts do.
 */
export function orgBadge(person: OutreachPerson, people: OutreachPerson[]): OrgBadge | null {
  const facts = person.playground;
  if (facts) {
    const paid = isPaidPlan(facts.tier);
    return {
      kind: paid ? 'org' : 'playground', label: paid ? 'ORG' : 'PG',
      orgName: facts.org_name, plan: facts.tier, role: facts.is_admin ? 'admin' : facts.org_role,
      credits30: facts.credits_30, lastUsage: facts.last_usage_on,
      users: facts.org_name ? orgUserCount(people, facts.org_name) : null,
    };
  }

  // No product facts: the Salesforce contact's org counts are all there is.
  const orgCount = person.contact?.orgs ?? 0;
  if (!orgCount) return null;
  const paidCount = person.contact?.paid_orgs ?? 0;
  return {
    kind: paidCount ? 'org' : 'playground', label: paidCount ? 'ORG' : 'PG',
    orgName: null, plan: paidCount ? `${paidCount} paid of ${orgCount}` : 'free',
    role: null, credits30: null, lastUsage: null, users: null,
  };
}

/** What each tag means, for its hover card. An unknown kind gets no explanation. */
export const TAG_MEANINGS: Record<string, string> = {
  playground_user: 'Uses Cartesia in the playground or through the API.',
  org_admin: 'Admin of a Cartesia org.',
  replied: 'Has replied to us.',
  warm_path: 'A warm path in: a colleague, investor or customer knows them.',
  job_changer: 'Recently changed jobs.',
};

export interface PersonChip {
  kind: 'flag' | 'sequence' | 'tag' | 'scout';
  // The flag's words, the sequence's name, the signal kind, or the day Scout found them.
  text: string;
}

/**
 * The one chip a table row has room for. A reason not to write beats a sequence already
 * running, which beats a reason to write: the row must never show "replied" over "opted out".
 */
export function personChip(person: OutreachPerson): PersonChip | null {
  const flag = person.flags[0];
  if (flag) return { kind: 'flag', text: flag };
  if (person.sequence) return { kind: 'sequence', text: person.sequence };
  if (person.foundByScout) return { kind: 'scout', text: shortDay(person.foundByScout.found_on) };
  const tag = person.tags[0];
  return tag ? { kind: 'tag', text: tag } : null;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10-04" as "4 Oct", for a chip; anything else as empty. */
export function shortDay(isoDay: string | null | undefined): string {
  const match = /^\d{4}-(\d{2})-(\d{2})/.exec(isoDay ?? '');
  if (!match) return '';
  const month = MONTHS[Number(match[1]) - 1];
  return month ? `${Number(match[2])} ${month}` : '';
}

/** A found row's short label: Q4 research, the extension's Enrich, or a Scout search. */
export function foundLabel(found: FoundByScout): string {
  if (isQ4Hero(found)) return 'Q4';
  if (isEnrichedFind(found)) return 'Enriched';
  return 'Scout';
}

/** Where a Scout find came from, for its hover card: vendor, day, and what Salesforce did. A Q4
 * research hero says that instead of "Found by Scout"; its reason is the card's prose. A person
 * enriched from their LinkedIn profile says so. */
export function foundByScoutRows(found: FoundByScout): Array<[string, string]> {
  if (isEnrichedFind(found)) return [['Source', 'Enriched from LinkedIn (Apollo)'], ...foundOutcomeRows(found)];
  const rows: Array<[string, string]> = isQ4Hero(found) ? [['Source', 'Q4 research']] : [['Found by', 'Scout']];
  if (found.source && !isQ4Hero(found)) rows.push(['Source', found.source.slice(0, 1).toUpperCase() + found.source.slice(1)]);
  return [...rows, ...foundOutcomeRows(found)];
}

// The day, what Salesforce did with the save, and whether they may be sent to.
function foundOutcomeRows(found: FoundByScout): Array<[string, string]> {
  const rows: Array<[string, string]> = [];
  if (found.found_on) rows.push(['Found', found.found_on]);
  rows.push(['Salesforce', SALESFORCE_OUTCOME_WORDS[found.sync_outcome ?? ''] ?? 'not saved yet']);
  rows.push(['Send', SEND_STATUS_WORDS[found.send_status ?? ''] ?? 'not cleared to send']);
  return rows;
}

// Whether they may be sent to, in words; anything else reads "not cleared to send".
const SEND_STATUS_WORDS: Record<string, string> = {
  cleared: 'cleared',
  dry_run_only: 'dry run only (preview)',
};

// What happened to the Salesforce save, in words; anything else reads "not saved yet".
const SALESFORCE_OUTCOME_WORDS: Record<string, string> = {
  created: 'saved, waiting for the next refresh',
  updated: 'saved, waiting for the next refresh',
  dry_run: 'held back (preview dry run)',
  withheld: 'held back by the write guard',
  failed: 'save not confirmed',
};

/** "name not revealed yet" for a masked Apollo name ("Ravi Pe***r"); null for anyone else. */
export function nameNote(person: OutreachPerson): string | null {
  return isMaskedName(person.contact?.name) && !person.named ? 'name not revealed yet' : null;
}

/** Whether a person has the two ways to reach them. */
export function reachOf(person: OutreachPerson): { email: boolean; linkedin: boolean } {
  return { email: Boolean(person.email), linkedin: Boolean(person.linkedin) };
}
